// Search result rows for the palette. Message and file rows share one
// `ResultRow` frame so they cannot drift on the keyboard + ARIA contract.

import { memo, type CSSProperties, type ReactNode } from "react";
import { File } from "lucide-react";

import { getInitials } from "~/lib/initials";
import { ICON_STROKE } from "~/lib/icon";

const resultRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--chat-space-3)",
  height: "var(--chat-search-dialog-result-row-height)",
  padding: "0 var(--chat-space-4)",
  cursor: "pointer",
};

// Owns the row's `role="option"` / active / hover-surface contract.
// Keyboard activation is handled at the dialog level, so the row needs
// only the click handler.
function ResultRow({
  id,
  active,
  onJump,
  children,
}: {
  id?: string;
  active: boolean;
  onJump: () => void;
  children: ReactNode;
}) {
  return (
    <div
      id={id}
      role="option"
      aria-selected={active}
      className="chat-search-result"
      data-active={active ? "1" : undefined}
      onClick={onJump}
      style={resultRowStyle}
    >
      {children}
    </div>
  );
}

export interface SearchResultItemProps {
  /** DOM id so the parent input can point `aria-activedescendant` at
   *  this row during keyboard navigation. Optional — stories without
   *  a surrounding combobox may omit it. */
  id?: string;
  /** Sender display name. Falls back to the MXID localpart when the
   *  server didn't surface a name. */
  senderDisplayName: string;
  /** Pre-formatted timestamp string. Keeps formatting concerns at the
   *  parent where i18n / locale decisions live. */
  time: string;
  /** Room the message was sent in. Restores the per-room context the
   *  dimension grouping no longer carries in a header. Optional. */
  roomName?: string;
  /** The message body. Matched tokens are bolded via `highlight`. */
  body: string;
  /** The lowercased query the row should bold inside `body`. Empty
   *  string renders the snippet plainly. */
  highlight: string;
  /** True when this row is the keyboard-active one (↑/↓ navigation). */
  active: boolean;
  /** Invoked on click or Enter. */
  onJump: () => void;
}

export const SearchResultItem = memo(function SearchResultItem({
  id,
  senderDisplayName,
  time,
  roomName,
  body,
  highlight,
  active,
  onJump,
}: SearchResultItemProps) {
  return (
    <ResultRow id={id} active={active} onJump={onJump}>
      <Avatar label={senderDisplayName} />
      <div data-feedback-redact="true" style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "var(--chat-space-2)",
            fontFamily: "var(--chat-font-sans)",
          }}
        >
          <span
            style={{
              color: "var(--chat-fg-primary)",
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              flex: 1,
            }}
          >
            {senderDisplayName}
          </span>
          {roomName ? (
            <span
              style={{
                color: "var(--chat-fg-muted)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "40%",
                flexShrink: 0,
              }}
            >
              {roomName}
            </span>
          ) : null}
          <span
            style={{
              color: "var(--chat-fg-secondary)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
              flexShrink: 0,
            }}
          >
            {time}
          </span>
        </div>
        <div
          style={{
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {renderHighlighted(body, highlight)}
        </div>
      </div>
    </ResultRow>
  );
});

export interface FileResultItemProps {
  /** DOM id so the parent input can point `aria-activedescendant` here. */
  id?: string;
  /** The attachment's filename — the file row's primary line. */
  fileName: string;
  /** Room the file was shared in — the file row's secondary line. */
  roomName: string;
  /** True when this row is the keyboard-active one (↑/↓ navigation). */
  active: boolean;
  /** Invoked on click or Enter. */
  onJump: () => void;
}

export const FileResultItem = memo(function FileResultItem({
  id,
  fileName,
  roomName,
  active,
  onJump,
}: FileResultItemProps) {
  return (
    <ResultRow id={id} active={active} onJump={onJump}>
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          flexShrink: 0,
          color: "var(--chat-fg-secondary)",
        }}
      >
        <File size={16} strokeWidth={ICON_STROKE} aria-hidden />
      </span>
      <div
        data-feedback-redact="true"
        style={{
          minWidth: 0,
          flex: 1,
          display: "flex",
          alignItems: "baseline",
          gap: "var(--chat-space-2)",
          fontFamily: "var(--chat-font-sans)",
        }}
      >
        <span
          style={{
            color: "var(--chat-fg-primary)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            flex: 1,
          }}
        >
          {fileName}
        </span>
        <span
          style={{
            color: "var(--chat-fg-secondary)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            flexShrink: 0,
          }}
        >
          {roomName}
        </span>
      </div>
    </ResultRow>
  );
});

/**
 * Split `body` around every case-insensitive occurrence of `needle` and
 * return a ReactNode list with the matches wrapped in `<mark>`. The
 * `<mark>` element is the HTML-native highlight tag and carries the
 * right ARIA semantics; we style it via inline styles so it reads
 * plainly across browsers and in print.
 *
 * Empty / whitespace-only `needle` short-circuits to the plain body —
 * there is nothing to highlight and the match loop would otherwise
 * match between every character (empty regex) or throw.
 */
function renderHighlighted(body: string, needle: string): ReactNode {
  const trimmed = needle.trim();
  if (trimmed.length === 0) return body;
  const re = new RegExp(`(${escapeRegex(trimmed)})`, "ig");
  const parts = body.split(re);
  return parts.map((part, i) =>
    part.toLowerCase() === trimmed.toLowerCase() ? (
      <mark
        key={i}
        style={{
          background: "transparent",
          color: "var(--chat-fg-primary)",
        }}
      >
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

/** Escape every regex metacharacter so a user query like `a.b(c)`
 *  doesn't throw at the RegExp call site. */
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function Avatar({ label }: { label: string }) {
  const initials = getInitials(label);
  return (
    <span
      aria-hidden
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        // 24×24 avatar pinned by the brief for this surface. Keeping
        // the raw px here rather than lifting into a token avoids
        // creating a one-use token just for the search dialog.
        width: 24,
        height: 24,
        flexShrink: 0,
        borderRadius: "var(--chat-radius-full)",
        background: "var(--chat-surface-panel)",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-xs-size)",
      }}
    >
      {initials || "–"}
    </span>
  );
}
